import { execFileSync } from "node:child_process";
import { existsSync, mkdirSync, readFileSync, writeFileSync } from "node:fs";
import { homedir } from "node:os";
import { dirname, join } from "node:path";

/**
 * Wire hooks into agent config files.
 *
 * For Devin CLI: adds hooks to ~/.config/devin/config.json under the "hooks" key.
 * For Claude Code: adds hooks to ~/.claude/settings.json under the "hooks" key.
 * For ZCode: adds hooks to ~/.zcode/cli/config.json under "hooks.events", and sets
 *   hooks.enabled = true (configuration-file hooks are disabled by default).
 *
 * Hooks installed:
 * - SessionStart: runs `remem-mcp hook-recall` → injects recent memory into agent context
 * - SessionEnd: runs `remem-mcp hook-session-end` → silently captures session summary to memory DB
 */

/**
 * Resolve the best command to invoke remem-mcp hooks.
 * If the binary is globally installed, use it directly (fast, no npx overhead).
 * Fall back to npx --prefer-offline (uses cache, avoids re-download).
 */
function hookCommand(subcommand: string): string {
  const globalKey = process.env.REMEM_GLOBAL_SESSION_KEY;
  const globalEnvPrefix = globalKey
    ? `env REMEM_GLOBAL_SESSION_KEY='${globalKey.replaceAll("'", `'\\''`)}' `
    : "";
  try {
    const binPath = execFileSync("which", ["remem-mcp"], { encoding: "utf-8" }).trim();
    if (binPath && existsSync(binPath)) {
      return `${globalEnvPrefix}${binPath} ${subcommand}`;
    }
  } catch {
    // Binary not found — fall back to npx
  }
  return `${globalEnvPrefix}npx --prefer-offline -y remem-mcp ${subcommand}`;
}

const CODEX_HOOK_SUBCOMMANDS = [
  "hook-recall",
  "hook-pre-tool-use",
  "hook-post-tool-use",
  "hook-post-compaction",
  "hook-user-prompt",
  "hook-stop",
  "hook-session-end",
];

/**
 * Codex may rewrite config.toml and remove comments, so marker comments are not
 * a reliable installation record. Detect the hook commands themselves.
 */
function hasCodexRememHooks(content: string): boolean {
  return CODEX_HOOK_SUBCOMMANDS.some((subcommand) => content.includes(` ${subcommand}"`));
}

/** Remove remem-mcp hook blocks, including marker-less TOML written by Codex. */
export function removeCodexHooksFromToml(content: string): string {
  const lines = content.split(/\r?\n/);
  const output: string[] = [];
  let index = 0;

  while (index < lines.length) {
    const match = lines[index].match(/^\[\[hooks\.([A-Za-z]+)\]\]\s*$/);
    if (!match) {
      output.push(lines[index]);
      index++;
      continue;
    }

    const event = match[1];
    const nestedPrefix = `[[hooks.${event}.hooks]]`;
    let end = index + 1;
    while (end < lines.length) {
      const line = lines[end];
      if (line.startsWith(nestedPrefix)) {
        end++;
        continue;
      }
      if (/^\[./.test(line)) break;
      end++;
    }

    const block = lines.slice(index, end);
    if (CODEX_HOOK_SUBCOMMANDS.some((subcommand) => block.includes(` ${subcommand}"`))) {
      // Remove comments immediately attached to this generated block.
      while (
        output.length > 0 &&
        (output[output.length - 1].trim() === "" ||
          (/^\s*#/.test(output[output.length - 1]) &&
            output[output.length - 1].includes("remem-mcp")))
      ) {
        output.pop();
      }
    } else {
      output.push(...block);
    }

    index = end;
  }

  return output
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trimEnd();
}

/** Hooks configuration for Devin CLI and Codex CLI.
 *  Note: Devin CLI does NOT support PreCompact — only PostCompaction. */
const HOOKS_CONFIG = {
  SessionStart: [
    {
      hooks: [
        {
          type: "command",
          command: hookCommand("hook-recall"),
          timeout: 10,
        },
      ],
    },
  ],
  PostToolUse: [
    {
      matcher: "Bash|exec",
      hooks: [
        {
          type: "command",
          command: hookCommand("hook-post-tool-use"),
          timeout: 5,
        },
      ],
    },
  ],
  // PostCompaction: re-inject memory after context compaction.
  // Supported by all 3 agents (Claude Code, Devin CLI, Codex CLI).
  // Note: Devin CLI and Codex CLI do NOT support PreCompact — only PostCompaction.
  // Claude Code supports both, but PostCompaction alone is sufficient.
  PostCompaction: [
    {
      hooks: [
        {
          type: "command",
          command: hookCommand("hook-post-compaction"),
          timeout: 10,
        },
      ],
    },
  ],
  UserPromptSubmit: [
    {
      hooks: [
        {
          type: "command",
          command: hookCommand("hook-user-prompt"),
          timeout: 5,
        },
      ],
    },
  ],
  SessionEnd: [
    {
      hooks: [
        {
          type: "command",
          command: hookCommand("hook-session-end"),
          timeout: 10,
        },
      ],
    },
  ],
};

/** Safely read and parse a JSON config file. Returns {} if file doesn't exist or is invalid. */
function readJsonConfig(path: string): Record<string, unknown> {
  if (!existsSync(path)) return {};
  try {
    return JSON.parse(readFileSync(path, "utf-8"));
  } catch {
    return {};
  }
}

/** Write JSON config file, creating directories as needed. */
function writeJsonConfig(path: string, data: unknown): void {
  const dir = dirname(path);
  if (!existsSync(dir)) mkdirSync(dir, { recursive: true });
  writeFileSync(path, `${JSON.stringify(data, null, 2)}\n`, "utf-8");
}

/** Merge hooks into an existing config object without overwriting other keys.
 * For each hook event, appends our hooks to any existing ones (preserves user hooks).
 */
function mergeHooks(
  config: Record<string, unknown>,
  hooks: Record<string, unknown>,
): Record<string, unknown> {
  const existing = (config.hooks as Record<string, unknown>) ?? {};
  const merged: Record<string, unknown> = { ...existing };

  for (const [event, newEntries] of Object.entries(hooks)) {
    const existingEntries = (existing[event] as unknown[]) ?? [];
    // Filter out any previous remem-mcp hooks for this event (avoid duplicates on re-install)
    const filtered = existingEntries.filter((entry) => {
      const hooks = (entry as { hooks?: { command?: string }[] })?.hooks;
      if (!hooks) return true;
      return !hooks.some((h) => h?.command?.includes("remem-mcp"));
    });
    merged[event] = [...filtered, ...(newEntries as unknown[])];
  }

  return {
    ...config,
    hooks: merged,
  };
}

/** Install hooks for Devin CLI. */
function installDevinHooks(): boolean {
  const configPath = join(homedir(), ".config", "devin", "config.json");

  if (!existsSync(dirname(configPath))) {
    return false;
  }

  const config = readJsonConfig(configPath);
  const updated = mergeHooks(config, HOOKS_CONFIG);
  writeJsonConfig(configPath, updated);
  return true;
}

/** Install hooks for Claude Code.
 *  Claude Code supports PostCompact (after compaction). */
function installClaudeCodeHooks(): boolean {
  const settingsPath = join(homedir(), ".claude", "settings.json");

  // Check if Claude Code is installed
  const claudeDir = join(homedir(), ".claude");
  if (!existsSync(claudeDir)) {
    return false;
  }

  // Claude Code uses PostCompact (not PostCompaction)
  const { PostCompaction: _omit, ...baseWithoutPostCompaction } = HOOKS_CONFIG;
  const claudeHooksConfig = {
    ...baseWithoutPostCompaction,
    PostCompact: [
      {
        hooks: [
          {
            type: "command",
            command: hookCommand("hook-post-compaction"),
            timeout: 10,
          },
        ],
      },
    ],
  };

  const config = readJsonConfig(settingsPath);
  const updated = mergeHooks(config, claudeHooksConfig);
  writeJsonConfig(settingsPath, updated);
  return true;
}

/** Install hooks for Codex CLI (TOML config). */
function installCodexHooks(): boolean {
  const configPath = join(homedir(), ".codex", "config.toml");

  if (!existsSync(configPath)) {
    return false;
  }

  let content = readFileSync(configPath, "utf-8");

  // Check if remem-mcp hooks are already installed
  if (hasCodexRememHooks(content)) {
    return true;
  }

  // Append remem-mcp hooks to the TOML config
  const hooksToml = `
# >>> remem-mcp SessionStart >>>
[[hooks.SessionStart]]
matcher = "startup|resume|clear|compact"

[[hooks.SessionStart.hooks]]
type = "command"
command = "${hookCommand("hook-recall")}"
timeout = 10
# <<< remem-mcp SessionStart <<<

# >>> remem-mcp PostToolUse >>>
[[hooks.PostToolUse]]
matcher = "Bash|exec"

[[hooks.PostToolUse.hooks]]
type = "command"
command = "${hookCommand("hook-post-tool-use")}"
timeout = 5
# <<< remem-mcp PostToolUse <<<

# >>> remem-mcp PostCompaction >>>
[[hooks.PostCompaction]]

[[hooks.PostCompaction.hooks]]
type = "command"
command = "${hookCommand("hook-post-compaction")}"
timeout = 10
# <<< remem-mcp PostCompaction <<<

# >>> remem-mcp UserPromptSubmit >>>
[[hooks.UserPromptSubmit]]

[[hooks.UserPromptSubmit.hooks]]
type = "command"
command = "${hookCommand("hook-user-prompt")}"
timeout = 5
# <<< remem-mcp UserPromptSubmit <<<

# >>> remem-mcp SessionEnd >>>
[[hooks.SessionEnd]]

[[hooks.SessionEnd.hooks]]
type = "command"
command = "${hookCommand("hook-session-end")}"
timeout = 10
# <<< remem-mcp SessionEnd <<<
`;

  content = content.trimEnd() + "\n" + hooksToml;
  writeFileSync(configPath, content, "utf-8");
  return true;
}

/** Install hooks for ZCode.
 *
 * ZCode stores configuration-file hooks at ~/.zcode/cli/config.json under
 * `hooks.events.<Event>` (note the extra nesting under "events"), and they are
 * disabled by default — `hooks.enabled: true` must be set or nothing fires.
 *
 * ZCode supports exactly seven events: SessionStart, UserPromptSubmit,
 * PreToolUse, PermissionRequest, PostToolUse, PostToolUseFailure, Stop.
 * It has no PostCompaction/PostCompact and no SessionEnd, so the session-end
 * capture is wired to Stop instead.
 */
function installZcodeHooks(): boolean {
  const configPath = join(homedir(), ".zcode", "cli", "config.json");

  // Install when ZCode has been run (~/.zcode exists). writeJsonConfig creates
  // cli/config.json if the desktop app hasn't written it yet.
  if (!existsSync(join(homedir(), ".zcode"))) {
    return false;
  }

  const config = readJsonConfig(configPath);

  // ZCode supports neither PostCompaction nor SessionEnd; route session-end
  // capture to Stop instead. PreToolUse/UserPromptSubmit/PostToolUse map 1:1.
  const { PostCompaction: _omit, SessionEnd: _omit2, ...baseConfig } = HOOKS_CONFIG;
  const zcodeHooksConfig = {
    ...baseConfig,
    Stop: [
      {
        hooks: [
          {
            type: "command",
            command: hookCommand("hook-stop"),
            timeout: 10,
          },
        ],
      },
    ],
  };

  const updated = mergeZcodeHooks(config, zcodeHooksConfig);
  writeJsonConfig(configPath, updated);
  return true;
}

/** Merge hooks into a ZCode config object under hooks.events.
 * Same merge semantics as mergeHooks (append, dedupe prior remem-mcp hooks),
 * but writes into the nested `hooks.events` key and force-enables the runner. */
function mergeZcodeHooks(
  config: Record<string, unknown>,
  hooks: Record<string, unknown>,
): Record<string, unknown> {
  const existing = (config.hooks as Record<string, unknown>) ?? {};
  const existingEvents = (existing.events as Record<string, unknown>) ?? {};
  const merged: Record<string, unknown> = { ...existingEvents };

  for (const [event, newEntries] of Object.entries(hooks)) {
    const existingEntries = (existingEvents[event] as unknown[]) ?? [];
    // Filter out any previous remem-mcp hooks for this event (avoid duplicates on re-install)
    const filtered = existingEntries.filter((entry) => {
      const hooks = (entry as { hooks?: { command?: string }[] })?.hooks;
      if (!hooks) return true;
      return !hooks.some((h) => h?.command?.includes("remem-mcp"));
    });
    merged[event] = [...filtered, ...(newEntries as unknown[])];
  }

  return {
    ...config,
    hooks: {
      ...existing,
      enabled: true, // configuration-file hooks are disabled by default in ZCode
      events: merged,
    },
  };
}

/** Install auto-capture hooks for supported agents. */
export async function installHooks(): Promise<void> {
  // Clean up old hooks first (handles upgrades where hooks were removed)
  await uninstallHooks(true);

  const names: string[] = [];

  if (installDevinHooks()) names.push("Devin CLI");
  if (installClaudeCodeHooks()) names.push("Claude Code");
  if (installZcodeHooks()) names.push("ZCode");
  if (installCodexHooks()) names.push("Codex CLI");

  if (names.length === 0) {
    console.log("No supported agents found.");
    console.log(
      "Install ZCode, Devin CLI, Claude Code, or Codex CLI first, then run this command again.",
    );
    return;
  }

  console.log(`Hooks: ${names.join(", ")}`);
}

/** Remove hooks from agent config files. */
export async function uninstallHooks(calledFromInstall = false): Promise<void> {
  let removed = 0;
  const rememEvents = [
    "SessionStart",
    "SessionEnd",
    "Stop",
    "PreToolUse",
    "PostToolUse",
    "PreCompact",
    "PostCompact",
    "PostCompaction",
    "UserPromptSubmit",
  ];

  // Remove from Devin CLI
  const devinPath = join(homedir(), ".config", "devin", "config.json");
  if (existsSync(devinPath)) {
    const config = readJsonConfig(devinPath);
    if (config.hooks) {
      const hooks = config.hooks as Record<string, unknown[]>;
      for (const ev of rememEvents) {
        if (hooks[ev]) {
          // Filter out only remem-mcp hooks, preserve user hooks
          hooks[ev] = (hooks[ev] as { hooks?: { command?: string }[] }[]).filter((entry) => {
            const entryHooks = entry?.hooks;
            if (!entryHooks) return true;
            return !entryHooks.some((h) => h?.command?.includes("remem-mcp"));
          });
          if (Array.isArray(hooks[ev]) && hooks[ev].length === 0) {
            delete hooks[ev];
          }
        }
      }
      if (Object.keys(hooks).length === 0) {
        delete config.hooks;
      }
      writeJsonConfig(devinPath, config);
      removed++;
    }
  }

  // Remove from Claude Code
  const claudePath = join(homedir(), ".claude", "settings.json");
  if (existsSync(claudePath)) {
    const config = readJsonConfig(claudePath);
    if (config.hooks) {
      const hooks = config.hooks as Record<string, unknown[]>;
      for (const ev of rememEvents) {
        if (hooks[ev]) {
          hooks[ev] = (hooks[ev] as { hooks?: { command?: string }[] }[]).filter((entry) => {
            const entryHooks = entry?.hooks;
            if (!entryHooks) return true;
            return !entryHooks.some((h) => h?.command?.includes("remem-mcp"));
          });
          if (Array.isArray(hooks[ev]) && hooks[ev].length === 0) {
            delete hooks[ev];
          }
        }
      }
      if (Object.keys(hooks).length === 0) {
        delete config.hooks;
      }
      writeJsonConfig(claudePath, config);
      removed++;
    }
  }

  // Remove from ZCode (hooks nested under hooks.events)
  const zcodePath = join(homedir(), ".zcode", "cli", "config.json");
  if (existsSync(zcodePath)) {
    const config = readJsonConfig(zcodePath);
    const hooks = (config.hooks as Record<string, unknown>) ?? {};
    const events = (hooks.events as Record<string, unknown[]>) ?? {};
    let changed = false;

    for (const ev of rememEvents) {
      if (events[ev]) {
        events[ev] = (events[ev] as { hooks?: { command?: string }[] }[]).filter((entry) => {
          const entryHooks = entry?.hooks;
          if (!entryHooks) return true;
          return !entryHooks.some((h) => h?.command?.includes("remem-mcp"));
        });
        if (Array.isArray(events[ev]) && events[ev].length === 0) {
          delete events[ev];
        }
        changed = true;
      }
    }

    if (changed) {
      if (Object.keys(events).length === 0) {
        delete hooks.events;
        // events was the only payload; "enabled" alone runs nothing, so drop it.
        delete hooks.enabled;
      } else {
        hooks.events = events;
      }
      if (Object.keys(hooks).length === 0) {
        delete config.hooks;
      } else {
        config.hooks = hooks;
      }
      writeJsonConfig(zcodePath, config);
      removed++;
    }
  }

  // Remove from Codex CLI (TOML config)
  const codexPath = join(homedir(), ".codex", "config.toml");
  if (existsSync(codexPath)) {
    let content = readFileSync(codexPath, "utf-8");
    const cleaned = removeCodexHooksFromToml(content);
    if (cleaned !== content) {
      content = cleaned;
      writeFileSync(codexPath, content.trimEnd() + "\n", "utf-8");
      removed++;
    }
  }

  if (removed > 0 && !calledFromInstall) {
    console.log(`Hooks removed from ${removed} agent(s).`);
  }
}
